import { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MessageCircle, X, Send, Calendar, MapPin, User, Loader2 } from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Upcoming training dates and locations
const TRAINING_SCHEDULE = [
  { date: "2026-04-15", city: "Paris", region: "Île-de-France", type: "Niveau 1 - Découverte IA", spots: 3 },
  { date: "2026-04-17", city: "Lyon", region: "Auvergne-Rhône-Alpes", type: "Niveau 2 - Vibe Coding", spots: 5 },
  { date: "2026-04-22", city: "Bordeaux", region: "Nouvelle-Aquitaine", type: "Niveau 1 - Découverte IA", spots: 8 },
  { date: "2026-04-24", city: "Marseille", region: "PACA", type: "Niveau 2 - Agents IA", spots: 4 },
  { date: "2026-04-29", city: "Nantes", region: "Pays de la Loire", type: "Niveau 1 - Découverte IA", spots: 6 },
  { date: "2026-05-06", city: "Lille", region: "Hauts-de-France", type: "Niveau 3 - Expertise", spots: 2 },
  { date: "2026-05-08", city: "Toulouse", region: "Occitanie", type: "Niveau 2 - Vibe Coding", spots: 7 },
  { date: "2026-05-13", city: "Strasbourg", region: "Grand Est", type: "Niveau 1 - Découverte IA", spots: 5 },
  { date: "2026-05-15", city: "Rennes", region: "Bretagne", type: "Niveau 2 - Agents IA", spots: 4 },
  { date: "2026-05-20", city: "Nice", region: "PACA", type: "Niveau 1 - Découverte IA", spots: 6 },
];

const TRAINERS = [
  {
    name: "Jean-Baptiste GIRAUD",
    specialties: ["Vibe Coding", "Apps IA", "Formation dirigeants", "Emergent"],
    description: "Fondateur, 20 ans d'expérience, expert vibe coding et apps métier"
  },
  {
    name: "Thierry ASSONION",
    specialties: ["Renseignement", "Signaux faibles", "War games", "Stratégie", "Risques"],
    description: "Expert renseignement, École de Guerre, fondateur d'Anticiper"
  },
  {
    name: "Thibaut MILEWSKI",
    specialties: ["OSINT", "Données", "Simulation", "Analyse"],
    description: "Expert OSINT et simulation, développeur IA depuis 2023"
  }
];

const LucieChatbot = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([
    {
      role: "assistant",
      content: `Bonjour ! Je suis Lucie, votre assistante IA de l'Académie IA. 🤖

Je connais les dates et lieux de nos prochaines formations dans toute la France. Je peux vous aider à :

📅 Trouver une formation près de chez vous
🎯 Choisir le niveau adapté à vos besoins
👨‍🏫 Vous orienter vers le formateur idéal

Dites-moi où vous êtes et ce que vous recherchez !`
    }
  ]);
  const [input, setInput] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const sendMessage = async () => {
    if (!input.trim() || isLoading) return;

    const userMessage = input.trim();
    setInput("");
    setMessages(prev => [...prev, { role: "user", content: userMessage }]);
    setIsLoading(true);

    try {
      const response = await fetch(`${API_URL}/api/chatbot/message`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ 
          message: userMessage,
          conversation_history: messages.slice(-6) // Last 6 messages for context
        })
      });

      const data = await response.json();
      
      setMessages(prev => [...prev, { 
        role: "assistant", 
        content: data.response,
        trainings: data.suggested_trainings,
        trainer: data.suggested_trainer
      }]);
    } catch (error) {
      setMessages(prev => [...prev, { 
        role: "assistant", 
        content: "Désolée, j'ai eu un problème technique. Pouvez-vous reformuler votre question ?" 
      }]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('fr-FR', { 
      weekday: 'long', 
      day: 'numeric', 
      month: 'long' 
    });
  };

  return (
    <>
      {/* Chat Button */}
      <motion.button
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        whileHover={{ scale: 1.1 }}
        onClick={() => setIsOpen(true)}
        className={`fixed bottom-24 right-6 z-50 w-16 h-16 rounded-full bg-gradient-to-r from-cyan-500 to-blue-600 text-white shadow-lg shadow-cyan-500/30 flex items-center justify-center ${isOpen ? 'hidden' : ''}`}
        data-testid="chatbot-button"
      >
        <MessageCircle className="w-7 h-7" />
        <span className="absolute -top-1 -right-1 w-4 h-4 bg-green-500 rounded-full animate-pulse" />
      </motion.button>

      {/* Chat Window */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.95 }}
            className="fixed bottom-6 right-6 z-50 w-[380px] h-[550px] bg-[#0a0a0b] border border-cyan-500/30 rounded-2xl shadow-2xl shadow-cyan-500/20 flex flex-col overflow-hidden"
            data-testid="chatbot-window"
          >
            {/* Header */}
            <div className="bg-gradient-to-r from-cyan-600 to-blue-600 p-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
                  <span className="text-xl">🤖</span>
                </div>
                <div>
                  <h3 className="font-bold text-white">Lucie</h3>
                  <p className="text-xs text-cyan-100">Assistante IA • En ligne</p>
                </div>
              </div>
              <button 
                onClick={() => setIsOpen(false)}
                className="text-white/80 hover:text-white transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4">
              {messages.map((msg, i) => (
                <div key={i} className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}>
                  <div className={`max-w-[85%] ${
                    msg.role === "user" 
                      ? "bg-cyan-600 text-white rounded-2xl rounded-br-md" 
                      : "bg-white/10 text-gray-200 rounded-2xl rounded-bl-md"
                  } px-4 py-3`}>
                    <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                    
                    {/* Suggested Trainings */}
                    {msg.trainings && msg.trainings.length > 0 && (
                      <div className="mt-3 space-y-2">
                        {msg.trainings.map((t, j) => (
                          <div key={j} className="bg-black/30 rounded-lg p-2 text-xs">
                            <div className="flex items-center gap-2 text-cyan-400">
                              <Calendar className="w-3 h-3" />
                              <span>{formatDate(t.date)}</span>
                            </div>
                            <div className="flex items-center gap-2 text-gray-300 mt-1">
                              <MapPin className="w-3 h-3" />
                              <span>{t.city} • {t.type}</span>
                            </div>
                            <div className="text-green-400 mt-1">
                              {t.spots} places disponibles
                            </div>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Suggested Trainer */}
                    {msg.trainer && (
                      <div className="mt-3 bg-black/30 rounded-lg p-2 text-xs">
                        <div className="flex items-center gap-2 text-purple-400">
                          <User className="w-3 h-3" />
                          <span className="font-semibold">{msg.trainer.name}</span>
                        </div>
                        <p className="text-gray-400 mt-1">{msg.trainer.description}</p>
                      </div>
                    )}
                  </div>
                </div>
              ))}
              
              {isLoading && (
                <div className="flex justify-start">
                  <div className="bg-white/10 rounded-2xl rounded-bl-md px-4 py-3">
                    <Loader2 className="w-5 h-5 animate-spin text-cyan-400" />
                  </div>
                </div>
              )}
              
              <div ref={messagesEndRef} />
            </div>

            {/* Input */}
            <div className="p-4 border-t border-white/10">
              <div className="flex gap-2">
                <input
                  type="text"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyPress={handleKeyPress}
                  placeholder="Posez votre question..."
                  className="flex-1 bg-white/10 border border-white/20 rounded-full px-4 py-2 text-white placeholder-gray-500 text-sm focus:outline-none focus:border-cyan-500"
                  data-testid="chatbot-input"
                />
                <button
                  onClick={sendMessage}
                  disabled={isLoading || !input.trim()}
                  className="w-10 h-10 rounded-full bg-cyan-600 hover:bg-cyan-500 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center transition-colors"
                  data-testid="chatbot-send"
                >
                  <Send className="w-4 h-4 text-white" />
                </button>
              </div>
              <p className="text-xs text-gray-500 text-center mt-2">
                Lucie peut vous aider à trouver la formation idéale
              </p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default LucieChatbot;
